import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { NotificationService } from '../../../../shared/services/notification.service';
import { SubmittedErrorStateMatcher } from '../../../../shared/utils/submitted-error-state.matcher';
import { legacyErrorMessage } from '../../../../shared/utils/legacy-api-response.util';
import { trimmedRequiredValidator } from '../../../../shared/utils/bank-validators.util';
import { CashCrudService } from '../../services/cash-crud.service';
import { CashEntity, CashFormDialogData, CashPayload } from '../../models/cash-form.model';
import { InputConfig } from '../../../../../shared/ui/form/input/input.component.config';

type CashControl = 'nombre' | 'ubicacion' | 'fondoDeCaja';

@Component({
  selector: 'app-cash-form-dialog',
  templateUrl: './cash-form-dialog.component.html',
  styleUrls: ['./cash-form-dialog.component.scss'],
  standalone: false,
  providers: [
    SubmittedErrorStateMatcher,
    { provide: ErrorStateMatcher, useExisting: SubmittedErrorStateMatcher },
  ],
})
export class CashFormDialogComponent implements OnInit {
  readonly form = new FormGroup({
    nombre: new FormControl('', { nonNullable: true, validators: [trimmedRequiredValidator] }),
    ubicacion: new FormControl('', { nonNullable: true, validators: [trimmedRequiredValidator] }),
    fondoDeCaja: new FormControl<number | null>(0),
  });

  entity: CashEntity | null = null;

  readonly nombreConfig: InputConfig = {
    label: 'Nombre',
    maxLength: 50,
    errors: { required: 'Indica el nombre de la caja' },
  };

  readonly ubicacionConfig: InputConfig = {
    label: 'Ubicación',
    maxLength: 100,
    errors: { required: 'Indica la ubicación' },
  };

  readonly fondoConfig: InputConfig = {
    label: 'Fondo de caja',
    type: 'number',
    inputmode: 'decimal',
    step: '0.01',
    suffix: '€',
  };

  ready = false;
  loadError = '';
  saving = false;
  submitted = false;
  saveError = '';
  showUid = false;

  static open(dialog: MatDialog, data: CashFormDialogData): Observable<boolean> {
    return dialog.open<CashFormDialogComponent, CashFormDialogData, boolean>(CashFormDialogComponent, {
      width: '560px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    }).afterClosed().pipe(map(result => result === true));
  }

  constructor(
    private dialogRef: MatDialogRef<CashFormDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: CashFormDialogData,
    private crud: CashCrudService,
    private notify: NotificationService,
    private errorState: SubmittedErrorStateMatcher,
  ) { }

  get isEdit(): boolean {
    return !!this.data && !!this.data.uid;
  }

  get subtitle(): string {
    if (this.isEdit && this.entity && this.entity.accountingModelsNumber) return 'Cuenta contable ' + this.entity.accountingModelsNumber;
    return this.isEdit ? 'Datos de la caja' : 'Se creará su cuenta contable 570 automáticamente';
  }

  hasError(control: CashControl, error: string): boolean {
    return this.submitted && this.form.controls[control].hasError(error);
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loadError = '';
    if (!this.isEdit) {
      this.applyEntity(null);
      this.ready = true;
      return;
    }

    const initial = this.data.cash;
    if (initial && initial.uid === this.data.uid) {
      this.data.cash = null;
      this.applyEntity(initial);
      this.ready = true;
      return;
    }

    this.ready = false;
    this.crud.get(this.data.uid as string)
      .then(entity => this.applyEntity(entity))
      .catch(error => { this.loadError = legacyErrorMessage(error, 'No se pudo cargar la caja.'); })
      .then(() => { this.ready = true; });
  }

  save(): void {
    if (this.saving) return;
    this.submitted = true;
    this.errorState.submitted = true;
    this.saveError = '';
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const payload = this.buildPayload();
    const uid = this.isEdit ? payload.uid || (this.data.uid as string) : null;

    this.saving = true;
    this.dialogRef.disableClose = true;

    const request = uid ? this.crud.update(uid, payload) : this.crud.create(payload);
    request
      .then(() => {
        this.notify.ok(uid ? 'Caja actualizada correctamente' : 'Caja creada correctamente');
        this.dialogRef.disableClose = false;
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.saveError = legacyErrorMessage(error, uid ? 'Ha ocurrido un error inesperado al actualizar la caja' : 'Ha ocurrido un error inesperado al crear la caja');
      });
  }

  cancel(): void {
    if (this.saving) return;
    this.dialogRef.close(false);
  }

  private applyEntity(source: CashEntity | null): void {
    this.entity = source ? {
      uid: source.uid,
      accountingModelsNumber: source.accountingModelsNumber || '',
      nombre: source.nombre || '',
      ubicacion: source.ubicacion || '',
      saldoContable: Number(source.saldoContable) || 0,
      fondoDeCaja: Number(source.fondoDeCaja) || 0,
    } : null;
    this.form.reset({
      nombre: this.entity ? this.entity.nombre : '',
      ubicacion: this.entity ? this.entity.ubicacion : '',
      fondoDeCaja: this.entity ? this.entity.fondoDeCaja : 0,
    });
  }

  private buildPayload(): CashPayload {
    const value = this.form.getRawValue();
    const base = this.entity;
    return {
      uid: base ? base.uid : '',
      accountingModelsNumber: base ? base.accountingModelsNumber : '',
      nombre: value.nombre.trim(),
      ubicacion: value.ubicacion.trim(),
      saldoContable: base ? base.saldoContable : 0,
      fondoDeCaja: Number(value.fondoDeCaja) || 0,
      manualMovement: null,
    };
  }
}
